'use client'

import { featureFlagsAtom } from '@polaris/core/stores'
import {
  formatCoin,
  hexToRgba,
  limitDecimals,
  shorten,
} from '@polaris/core/utils'
import { ClientOnly } from '@quirks/react'
import { useAtomValue, useSetAtom } from 'jotai'
import { Ban, ChevronDown, Plus, WandSparkles } from 'lucide-react'
import Image from 'next/image'
import { useMemo } from 'react'
import { CounterBadge } from '@/src/components/ui/counter-badge'
import { useFilteredBalances } from '@/src/queries/hooks/balances'
import {
  onboardingDialogAtom,
  openOnboardingDialog,
} from '@/src/stores/onboarding'
import { PRIVACY_UI } from '@/src/stores/privacy/constants'
import {
  openSelectWalletsDialog,
  selectWalletsDialogAtom,
} from '@/src/stores/select-wallets'
import { useSwapFormActions } from '@/src/stores/swap/actions'
import {
  destinationAccountStateAtom,
  fromAccountsBalancesAtom,
  fromBalanceAtom,
  fromTokenAcrossChainsAtom,
  getWalletAccountInfoAtom,
  isSelectingMultiAccountFromBalanceAtom,
  type MaxBalanceState,
  maxBalanceStateAtom,
  openDestinationAccount,
  swapRequirementsAtom,
  swapRouteAtom,
  toTokenAcrossChainsAtom,
  toWalletAddressAtom,
} from '@/src/stores/swap/atoms'
import { userAtom } from '@/src/stores/user/atoms'
import { cn } from '@/src/utils/styles'
import { isFeeToken } from '@/src/utils/token-utils'
import { WatchAddressIcon } from '../accounts/watch-address'
import {
  Tooltip,
  TooltipContent,
  TooltipDescription,
  TooltipProvider,
  TooltipTitle,
  TooltipTrigger,
} from '../ui/tooltip'
import { SendToAddressIcon } from './destination-account-select'

interface WalletProps {
  // Trade module direction
  direction: 'from' | 'to'
}
export const WalletBalance = ({ direction }: WalletProps) => {
  const { fromChainNameNotSupportedByWallet } =
    useAtomValue(swapRequirementsAtom)

  return (
    <ClientOnly>
      <TooltipProvider>
        <div className='flex items-center gap-3 flex-nowrap'>
          {direction === 'from' && !fromChainNameNotSupportedByWallet ? (
            <BalanceButton />
          ) : null}
          <BalanceWalletIcon direction={direction} />
        </div>
      </TooltipProvider>
    </ClientOnly>
  )
}

const BalanceButton = () => {
  const { setFromBalance } = useSwapFormActions()
  const token = useAtomValue(fromTokenAcrossChainsAtom)
  const route = useAtomValue(swapRouteAtom)
  const maxBalanceState = useAtomValue(maxBalanceStateAtom)
  const { balancesByChains } = useFilteredBalances({
    chainId: token.data?.requested_asset.chain_id,
  })

  const hasOtherChainBalances = balancesByChains[0]?.balances.length > 1
  const costCheckResult = route?.data?.route.FeeRequirements?.cost_check_result
  const tokenData = token.data?.requested_asset

  // Check if this token is a fee token
  let isTokenFeeToken = false
  if (costCheckResult && tokenData) {
    // Primary: Check if fee token matches current token (by denom or ticker, case-insensitive)
    isTokenFeeToken = !!(
      costCheckResult.fee.denom?.toLowerCase() ===
        tokenData.denom?.toLowerCase() ||
      costCheckResult.fee.ticker?.toLowerCase() ===
        tokenData.ticker?.toLowerCase()
    )
  }

  // Fallback: Use token metadata to determine if it's a native fee token
  // This helps when costCheckResult hasn't loaded yet
  if (!isTokenFeeToken && tokenData) {
    isTokenFeeToken = isFeeToken(tokenData)
  }

  const Content = useMemo(() => {
    // Type A: Non-fee token
    if (!isTokenFeeToken) {
      if (maxBalanceState === 'default') {
        // State 3: Before clicking "Use max"
        return (
          <TooltipContent hideArrow>
            <TooltipTitle>Use maximum balance</TooltipTitle>
          </TooltipContent>
        )
      }

      // State 4: After clicking "Use max"
      return (
        <TooltipContent hideArrow>
          <TooltipTitle>
            Your entire available {tokenData?.ticker} balance will be traded
          </TooltipTitle>
        </TooltipContent>
      )
    }

    // Type C: Fee token WITH other balances on same chain
    if (isTokenFeeToken && hasOtherChainBalances) {
      // State 12: Before clicking - show "Use available balance"
      if (maxBalanceState === 'default') {
        return (
          <TooltipContent hideArrow>
            <TooltipTitle>Use available balance</TooltipTitle>
          </TooltipContent>
        )
      }

      // State 13: First click
      if (maxBalanceState === 'first-click' && costCheckResult) {
        const dustAmount = costCheckResult?.follow_on_dust_amount?.native || 0
        return (
          <TooltipContent className='flex gap-3' hideArrow>
            <WandSparkles width={16} height={16} className='text-link' />
            <div className='flex flex-col gap-1'>
              <TooltipTitle>
                {costCheckResult?.fee.ticker} reserved for fees and future
                trades
              </TooltipTitle>
              <TooltipDescription className='font-normal break-words'>
                {formatCoin({
                  denom: costCheckResult?.fee.ticker,
                  amount: costCheckResult?.reserved_amount.native || 0,
                })}{' '}
                will be reserved to cover estimated fees for this trade.
                <br />
                <br />
                Additionally,{' '}
                {formatCoin({
                  denom: costCheckResult?.fee.ticker,
                  amount: dustAmount,
                })}{' '}
                will be saved for future transactions on {tokenData?.chain_name}
                .
              </TooltipDescription>
            </div>
          </TooltipContent>
        )
      }

      // State 14: Second click - red warning
      if (maxBalanceState === 'second-click' && costCheckResult) {
        return (
          <TooltipContent className='flex gap-3' hideArrow>
            <Ban width={16} height={16} className='text-danger-text' />
            <div className='flex flex-col gap-1'>
              <TooltipTitle>
                {costCheckResult?.fee.ticker} reserved for fees
              </TooltipTitle>
              <TooltipDescription className='font-normal break-words'>
                {formatCoin({
                  denom: costCheckResult?.fee.ticker,
                  amount: costCheckResult?.reserved_amount.native || 0,
                })}{' '}
                will be reserved to cover estimated fees for this trade.
                <br />
                <br />
                Your other balances on {tokenData?.chain_name} will become stuck
                with no {tokenData?.ticker} left to pay network fees.
              </TooltipDescription>
            </div>
          </TooltipContent>
        )
      }

      return null
    }

    // Type B: Fee token without other balances
    if (isTokenFeeToken) {
      // State 7: Before clicking "Use max"
      if (maxBalanceState === 'default') {
        return (
          <TooltipContent hideArrow>
            <TooltipTitle>Use maximum balance</TooltipTitle>
          </TooltipContent>
        )
      }

      // State 8: After clicking "Use max"
      if (costCheckResult) {
        return (
          <TooltipContent className='flex gap-3' hideArrow>
            <WandSparkles width={16} height={16} className='text-link' />
            <div className='flex flex-col gap-1'>
              <TooltipTitle>
                {costCheckResult?.fee.ticker} reserved for fees
              </TooltipTitle>
              <TooltipDescription className='font-normal break-words'>
                {formatCoin({
                  denom: costCheckResult?.fee.ticker,
                  amount: costCheckResult?.reserved_amount.native || 0,
                })}{' '}
                will be reserved to cover estimated fees for this trade.
              </TooltipDescription>
            </div>
          </TooltipContent>
        )
      }

      return null
    }

    // Fallback: No tooltip when no data
    if (!costCheckResult) {
      return (
        <TooltipContent hideArrow>
          <TooltipTitle>Use maximum balance</TooltipTitle>
        </TooltipContent>
      )
    }

    return null
  }, [
    isTokenFeeToken,
    costCheckResult,
    maxBalanceState,
    hasOtherChainBalances,
    tokenData?.chain_name,
    tokenData?.ticker,
  ])

  // Block button for Type C when costCheckResult is loading
  const isTypeC = isTokenFeeToken && hasOtherChainBalances
  const isBlocked = isTypeC && !costCheckResult

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <button
            type='button'
            className='select-none group'
            onClick={setFromBalance}
            disabled={isBlocked}
          >
            <BalanceAmount
              hasOtherChainBalances={hasOtherChainBalances}
              isFeeToken={isTokenFeeToken}
              maxBalanceState={maxBalanceState}
            />
          </button>
        </TooltipTrigger>
        {Content}
      </Tooltip>
    </TooltipProvider>
  )
}

const BalanceAmount = ({
  hasOtherChainBalances,
  isFeeToken,
  maxBalanceState,
}: {
  hasOtherChainBalances: boolean
  isFeeToken: boolean
  maxBalanceState: MaxBalanceState
}) => {
  const balance = useAtomValue(fromBalanceAtom)

  const balanceAmount = useMemo(
    () =>
      balance && balance.normalized_amount > 0
        ? limitDecimals(balance.normalized_amount)
        : undefined,
    [balance],
  )

  if (!balance || !balanceAmount || balanceAmount === '0') return null

  const isTypeC = hasOtherChainBalances && isFeeToken

  // Type C: Three-state cycle
  if (isTypeC) {
    if (maxBalanceState === 'first-click') {
      // State 10: First click - orange "Use max"
      return (
        <p className='text-link group-hover:text-link-hover text-sm leading-6 transition-colors'>
          Use max
        </p>
      )
    }
    if (maxBalanceState === 'second-click') {
      // State 11: Second click - red "Using max"
      return (
        <p className='text-danger-text group-hover:text-danger-text-hover text-sm leading-6 transition-colors'>
          Using max
        </p>
      )
    }
  }

  // Type A & B: Show "Using max" when in first-click state
  // Type A: non-fee token (regardless of other balances)
  // Type B: fee token without other balances
  if (maxBalanceState === 'first-click' && !isTypeC) {
    return (
      <p className='text-link group-hover:text-link-hover text-sm leading-6 transition-colors'>
        Using max
      </p>
    )
  }

  // Default: Show "X available"
  return (
    <p className='text-secondary text-sm leading-6'>
      <span
        data-testid='balance'
        className='text-link group-hover:text-link-hover leading-5 transition-colors'
      >
        {balanceAmount}
      </span>{' '}
      available
    </p>
  )
}

const BalanceWalletIcon = ({ direction }: WalletProps) => {
  const token = useAtomValue(
    direction === 'from' ? fromTokenAcrossChainsAtom : toTokenAcrossChainsAtom,
  )
  const user = useAtomValue(userAtom)
  const setDestinationAccountState = useSetAtom(destinationAccountStateAtom)
  const tokenChainType = token.data?.requested_asset.chain_type
  const fromAccountsBalances = useAtomValue(fromAccountsBalancesAtom)
  const toWalletAddress = useAtomValue(toWalletAddressAtom)
  const setIsSelectingMultiAccountFromBalance = useSetAtom(
    isSelectingMultiAccountFromBalanceAtom,
  )
  const { newLoginFlow } = useAtomValue(featureFlagsAtom)
  const setSelectWalletsDialogState = useSetAtom(selectWalletsDialogAtom)
  const setOnboardingDialogState = useSetAtom(onboardingDialogAtom)
  const walletInfo = useAtomValue(getWalletAccountInfoAtom)(direction)
  const fromBalance = useAtomValue(fromBalanceAtom)
  const { fromChainNameNotSupportedByWallet } =
    useAtomValue(swapRequirementsAtom)

  const isUnsupported =
    direction === 'from' && !!fromChainNameNotSupportedByWallet

  /** Connected wallet and/or saved wallet account would be populated as object. */
  const hasWalletOrAccount = Boolean(
    walletInfo && typeof walletInfo === 'object',
  )
  const isManualAddressInput = walletInfo === 'manual-address-input'

  const openSelector = () => {
    if (direction === 'from') {
      if (hasWalletOrAccount) {
        // From direction always opens multi-account balance selector
        setIsSelectingMultiAccountFromBalance(true)
      } else {
        if (user.id === 'guest' && newLoginFlow) {
          // Guest users should see onboarding dialog, which could encourage email login

          setOnboardingDialogState(openOnboardingDialog('wallets'))
        } else {
          // No wallet or account, show wallet selector
          setSelectWalletsDialogState(openSelectWalletsDialog(tokenChainType))
        }
      }
    } else {
      // To direction: always use new account selector with manual address input
      setDestinationAccountState(openDestinationAccount(tokenChainType))
    }
  }

  // Determine if the plus button should be shown
  // This should only show if there's no wallet account anywhere for this chain type
  let showPlusButton = false
  if (direction === 'to') {
    showPlusButton = !hasWalletOrAccount && !isManualAddressInput
  } else {
    // From
    showPlusButton = !hasWalletOrAccount
  }

  if (showPlusButton) {
    return (
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <button
            type='button'
            aria-label='Add wallet'
            onClick={openSelector}
            className='relative group/balance h-8 focus-outer rounded-lg'
          >
            <div className='size-8 flex items-center justify-center rounded-lg bg-button-secondary'>
              <Plus className='text-button-secondary-text size-6' />
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent side='right' className='flex'>
          <span className='text-xs font-semibold'>
            Connect {token.data?.requested_asset.chain_name} wallet
          </span>
        </TooltipContent>
      </Tooltip>
    )
  }

  const imageSrc =
    walletInfo && typeof walletInfo === 'object' ? walletInfo.img : undefined
  const imageAlt =
    walletInfo && typeof walletInfo === 'object' ? walletInfo.name : undefined
  const account =
    walletInfo &&
    typeof walletInfo === 'object' &&
    'account' in walletInfo &&
    walletInfo.account &&
    !('wallet' in walletInfo.account)
      ? walletInfo.account
      : undefined

  const selectedAccount = fromAccountsBalances.find(
    (account) => fromBalance?.address === account.address,
  )

  const isDisconnected = selectedAccount && !('wallet' in selectedAccount)

  // Disable button when multi-account selection is not allowed (e.g., Namada privacy vault)
  const isMultiAccountDisabled =
    walletInfo &&
    typeof walletInfo === 'object' &&
    walletInfo.isMultiAccount === false

  const buttonContent = (
    <>
      {walletInfo === 'manual-address-input' ? (
        <>
          {toWalletAddress && (
            <p className='text-sm text-tertiary whitespace-nowrap overflow-hidden'>
              {shorten(toWalletAddress, {
                prefixLength: 6,
                suffixLength: 4,
              })}
            </p>
          )}
          <SendToAddressIcon className='!size-8 min-w-8' size={20} />
        </>
      ) : (
        <>
          {account && (
            <p
              className={cn(
                'text-sm text-tertiary whitespace-nowrap overflow-hidden',
              )}
            >
              {account.name}
            </p>
          )}
          <div className='rounded-lg overflow-hidden bg-foreground'>
            <div
              className={cn('size-8 flex items-center justify-center', {
                'group-hover/balance:opacity-40 group-focus/balance:opacity-40':
                  !isMultiAccountDisabled,
                'bg-surface1': !(
                  walletInfo &&
                  typeof walletInfo === 'object' &&
                  walletInfo.color
                ),
              })}
              style={
                walletInfo && typeof walletInfo === 'object' && walletInfo.color
                  ? { backgroundColor: hexToRgba(walletInfo.color, 0.6) }
                  : undefined
              }
            >
              {imageSrc === 'no-logo' ? (
                <WatchAddressIcon size='sm' />
              ) : imageSrc ? (
                <Image
                  key={imageSrc}
                  alt={imageAlt || 'Wallet icon'}
                  src={imageSrc}
                  className={cn({
                    'w-6 h-6': imageSrc === PRIVACY_UI.POLARIS_LOGO, // Full size for Polaris logo
                    'w-3/4 h-auto': imageSrc !== PRIVACY_UI.POLARIS_LOGO,
                    'opacity-50': isDisconnected,
                    'opacity-30': isUnsupported,
                  })}
                  width={24}
                  height={24}
                  unoptimized
                />
              ) : null}
            </div>
            {isUnsupported && (
              <Ban
                size={24}
                strokeWidth={2}
                className='absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2'
              />
            )}
            {direction === 'from' &&
              !isUnsupported &&
              !isMultiAccountDisabled && (
                <ChevronDown
                  size={24}
                  className={cn(
                    'text-primary absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2',
                    'invisible group-hover/balance:visible group-focus/balance:visible',
                  )}
                />
              )}
          </div>
          {fromAccountsBalances.length > 1 &&
            direction === 'from' &&
            !isMultiAccountDisabled && (
              <CounterBadge
                className={cn(
                  'absolute -bottom-px -right-0.5 cursor-pointer',
                  'group-hover/balance:hidden group-focus/balance:hidden',
                )}
                count={fromAccountsBalances.length}
                disableHover
              />
            )}
        </>
      )}
    </>
  )

  const WalletIconButton = isMultiAccountDisabled ? (
    <div className='relative group/balance h-8 rounded-lg flex items-center gap-2'>
      {buttonContent}
    </div>
  ) : (
    <button
      type='button'
      onClick={openSelector}
      className='relative group/balance h-8 focus-outer rounded-lg flex items-center gap-2 cursor-pointer'
    >
      {buttonContent}
    </button>
  )

  if (selectedAccount && direction === 'from') {
    return (
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>{WalletIconButton}</TooltipTrigger>
        <TooltipContent side='right' className='flex flex-col gap-1'>
          <span className='text-xs font-semibold'>{selectedAccount.name}</span>
          <span className='text-xs text-secondary'>
            {shorten(selectedAccount.address, {
              prefixLength: 8,
              suffixLength: 4,
            })}
          </span>
        </TooltipContent>
      </Tooltip>
    )
  }

  // Can be no selected account if this is a connected wallet w/out account
  return WalletIconButton
}
